import {
  Box,
  CollectionPreferencesProps,
  PaginationProps,
  Popover,
  TableProps,
} from "@amzn/awsui-components-react";
import React, { useMemo } from "react";
import { extractYear } from "components/create-audit-form/utils";
import TableItemStatus from "components/table-item-status";
import { Link } from "react-router-dom";
import { useAppSelector } from "app/store";
import { optionLabel, optionWithGroupLabel } from "utils/dropdown-utils";
import { createLabelFunction, dateFieldComparator } from "utils/table-utils";
import { getProgressUpdated } from "./utils";
import styles from "./style.module.scss";
import { formatDateValue } from "utils/date-format";
import { attributeSchemaToColDef } from "utils/dynamicAttributesUtils";
import { TemplateOption } from "components/templates-table/definitions";
import { formatMonetaryValue } from "pages/audit-overview/definitions";
import { getCurrencySignFromCountry } from "features/ui-content/utils";
import { useGaiaObjectUiContent } from "features/ui-content/hooks";
import { TEAM_NAME_MAP } from "utils/gaia-constants";

// Common fields that are available for guest users across all teams
// all common fields are from non-custom attributes
export const COMMON_AUDIT_FIELDS = [
  "shortId",
  "name",
  "status",
  "owner",
  "statusActionOwner",
  "progressUpdated",
  "auditInitiationDate",
  "auditCloseDate",
  "statuteOfLimitations",
  "dateCreated",
  "lastModified",
];

export const GUEST_AUDIT_FIELDS = [...COMMON_AUDIT_FIELDS, "teamName"];

export const goToGaiaObjectOverviewPage = (item: any, category: string) => {
  return (
    <div>
      <Link
        to={`/${category.startsWith("a") ? category : category.concat("s")}/${
          item.gaiaObjectId
        }`}
        className="gaiaObject-overv"
        style={{ color: "#0972D3", textDecoration: "none" }}
      >
        {item.name}
      </Link>
    </div>
  );
};

export const goToAuditOverviewPage = (item: any) => {
  return (
    <div>
      <Link
        to={`/audit/${item.auditId}`}
        className="audit-overv"
        style={{ color: "#0972D3", textDecoration: "none" }}
      >
        {item.auditName}
      </Link>
    </div>
  );
};

export const getAuditPeriod = (item: any) => {
  return (
    extractYear(item.auditStartDate) + "-" + extractYear(item.auditEndDate)
  );
};

export const getStatusDetail = (statusDetail: string) => {
  const truncated =
    statusDetail.length > 200
      ? `${statusDetail.substring(0, 200)}...`
      : statusDetail;

  return <Popover content={<Box>{statusDetail}</Box>}>{truncated}</Popover>;
};

export const useRawColumnDefinitionsByTeam = () => {
  const teamName = useAppSelector((state) => state.audits.headerSelectedTeam);
  const uiContent = useGaiaObjectUiContent("audit");
  let rawColumnDefinitions: TableProps<any>["columnDefinitions"] = [
    {
      id: "shortId",
      header: "ID",
      cell: (item) => item.shortId,
      ariaLabel: createLabelFunction("ID"),
      sortingField: "shortId",
      sortingComparator: (a, b) => a.shortId.localeCompare(b.shortId),
      width: 160,
    },
    {
      id: "name",
      header: "Name",
      cell: (item) => goToAuditOverviewPage(item),
      ariaLabel: createLabelFunction("Name"),
      sortingField: "name",
      sortingComparator: (a, b) => a.auditName.localeCompare(b.auditName),
      width: 420,
    },
    {
      id: "status",
      header: "Status",
      cell: (item) => <TableItemStatus stringStatus={item.status} />,
      ariaLabel: createLabelFunction("Status"),
      sortingField: "status",
      width: 160,
    },
    {
      id: "owner",
      header: "Owner",
      cell: (item) => item.owner,
      ariaLabel: createLabelFunction("Owner"),
      sortingField: "owner",
      width: 160,
    },
    {
      id: "actionOwner",
      header: "Action pending",
      cell: (item) => item.statusActionOwner,
      ariaLabel: createLabelFunction("Action pending"),
      sortingField: "actionOwner",
      sortingComparator: (a, b) => {
        return a.statusActionOwner.localeCompare(b.statusActionOwner);
      },
      width: 160,
    },
    {
      id: "progressUpdated",
      header: "Progress updated",
      cell: (item) => getProgressUpdated(item),
      ariaLabel: createLabelFunction("Progress updated"),
      sortingField: "progressUpdated",
      sortingComparator: dateFieldComparator("progressModifiedAt"),
      width: 200,
    },
    {
      id: "statusDetail",
      header: "Progress detail",
      cell: (item) => getStatusDetail(item.statusDetail),
      ariaLabel: createLabelFunction("statusDetail"),
      sortingField: "statusDetail",
      width: 420,
    },
    {
      id: "auditPeriod",
      header: "Audit period",
      cell: (item) => getAuditPeriod(item),
      ariaLabel: createLabelFunction("Audit period"),
      sortingField: "auditPeriod",
      sortingComparator: (a, b) =>
        getAuditPeriod(a).localeCompare(getAuditPeriod(b)),
      width: 200,
    },
    {
      id: "taxType",
      header: "Tax type",
      cell: (item) => item.taxType?.join(", "),
      ariaLabel: createLabelFunction("Tax type"),
      sortingField: "taxType",
      width: 200,
    },
    {
      id: "entity",
      header: "Entity",
      cell: (item) => item.entity,
      ariaLabel: createLabelFunction("Entity"),
      sortingField: "entity",
      width: 200,
    },
    {
      id: "entityCode",
      header: "Entity code",
      cell: (item) => item.entityCode || "-",
      ariaLabel: createLabelFunction("Entity code"),
      sortingField: "entityCode",
      sortingComparator: (a, b) => {
        const aCode = a?.entityCode || "";
        const bCode = b?.entityCode || "";
        return aCode.localeCompare(bCode);
      },
      width: 200,
    },
    {
      id: "jurisdictionLevel",
      header: "Jurisdiction level",
      cell: (item) => item.jurisdictionLevel,
      ariaLabel: createLabelFunction("Jurisdiction level"),
      sortingField: "jurisdictionLevel",
      width: 200,
    },
    {
      id: "stateProvince",
      header: "State/Province",
      cell: (item) => {
        return optionWithGroupLabel(
          uiContent.governmentEntityOptions,
          item.governmentEntity,
          // when more than one country option, there will be a country associated with each option
          uiContent.countryOptions && uiContent.countryOptions.length > 1
            ? item.country
            : ""
        );
      },
      ariaLabel: createLabelFunction("State/Province"),
      sortingField: "stateProvince",
      sortingComparator: (a, b) =>
        a.governmentEntity.localeCompare(b.governmentEntity),
      width: 200,
    },
    {
      id: "localJurisdiction",
      header: "Local jurisdiction",
      cell: (item) => item.localJurisdiction,
      ariaLabel: createLabelFunction("Local jurisdiction"),
      sortingField: "localJurisdiction",
      width: 200,
    },
    {
      id: "authority",
      header: "Tax authority",
      cell: (item) => item.taxAuthority,
      ariaLabel: createLabelFunction("Tax authority"),
      sortingField: "authority",
      width: 200,
    },
    {
      id: "auditNumber",
      header: "Audit number",
      cell: (item) => item.auditNumber,
      ariaLabel: createLabelFunction("Audit number"),
      sortingField: "auditNumber",
      width: 200,
    },
    {
      id: "taxNumber",
      header: "Tax number",
      cell: (item) => item.taxNumber,
      ariaLabel: createLabelFunction("Tax number"),
      sortingField: "taxNumber",
      width: 200,
    },
    {
      id: "auditInitiationDate",
      header: "Audit initiation date",
      cell: (item) => formatDateValue(item.auditInitiationDate),
      ariaLabel: createLabelFunction("Audit initiation date"),
      sortingField: "auditInitiationDate",
      width: 200,
    },
    {
      id: "auditCloseDate",
      header: "Audit close date",
      cell: (item) => formatDateValue(item.auditCloseDate),
      ariaLabel: createLabelFunction("Audit close date"),
      sortingField: "auditCloseDate",
      width: 160,
    },
    {
      id: "statuteOfLimitations",
      header: "Statute of limitations",
      cell: (item) => formatDateValue(item.statuteOfLimitations),
      ariaLabel: createLabelFunction("Statute of limitations"),
      sortingField: "statuteOfLimitations",
      width: 200,
    },
    {
      id: "dateCreated",
      header: "Date created",
      cell: (item) => formatDateValue(item.createdAt),
      ariaLabel: createLabelFunction("Date created"),
      sortingField: "dateCreated",
      sortingComparator: dateFieldComparator("createdAt"),
      width: 160,
    },
    {
      id: "lastModified",
      header: "Date modified",
      cell: (item) => formatDateValue(item.modifiedAt),
      ariaLabel: createLabelFunction("Date modified"),
      sortingField: "lastModified",
      sortingComparator: dateFieldComparator("modifiedAt"),
      width: 160,
    },
    {
      id: "description",
      header: "Description",
      cell: (item) => item.description,
      ariaLabel: createLabelFunction("Description"),
      sortingField: "description",
      width: 200,
    },
    {
      id: "assessmentTotal",
      header: "Assessment total",
      cell: (item) =>
        item.assessmentTotal && item.assessmentTotal !== "0.0"
          ? formatMonetaryValue(
              item.assessmentTotal,
              getCurrencySignFromCountry(item.country, uiContent)
            )
          : "-",
      ariaLabel: createLabelFunction("Assessment Total"),
      sortingField: "assessmentTotal",
      width: 150,
    },
    {
      id: "paymentTotal",
      header: "Payment total",
      cell: (item) =>
        item.paymentTotal && item.paymentTotal !== "0.0"
          ? formatMonetaryValue(
              item.paymentTotal,
              getCurrencySignFromCountry(item.country, uiContent)
            )
          : "-",
      ariaLabel: createLabelFunction("Payment Total"),
      sortingField: "paymentTotal",
      width: 150,
    },
    {
      id: "progressModifiedBy",
      header: "Progress Update By",
      cell: (item) => item.progressModifiedBy,
      ariaLabel: createLabelFunction("Progress Update By"),
      sortingField: "progressModifiedBy",
      width: 200,
    },
    {
      id: "progressModifiedAt",
      header: "Progress Update At",
      cell: (item) => formatDateValue(item.progressModifiedAt),
      ariaLabel: createLabelFunction("Progress Update At"),
      sortingComparator: dateFieldComparator("progressModifiedAt"),
      width: 200,
    },
    {
      id: "auditPeriodStart",
      header: "Audit period start",
      cell: (item) => formatDateValue(item.auditStartDate),
      ariaLabel: createLabelFunction("Audit period start"),
      sortingField: "auditPeriodStart",
      width: 200,
    },
    {
      id: "auditPeriodEnd",
      header: "Audit period end",
      cell: (item) => formatDateValue(item.auditEndDate),
      ariaLabel: createLabelFunction("Audit period end"),
      sortingField: "auditPeriodEnd",
      width: 200,
    },
    {
      id: "effort",
      header: "Effort",
      cell: (item) => item?.customAttributesDataMap?.effort || "-",
      ariaLabel: createLabelFunction("Effort"),
      sortingField: "effort",
      sortingComparator: (a, b) =>
        Number(a.customAttributesDataMap?.effort || 0) -
        Number(b.customAttributesDataMap?.effort || 0),
      width: 120,
    },
  ];

  const auditCustomAttributes = useAppSelector(
    (state) =>
      state.teamSettings.selectedTeamMainMetaData.teamCustomAttributesSchema
        ?.audit
  );

  // For guest users, only return basic common columns without custom attributes
  if (teamName === "guest") {
    rawColumnDefinitions = [
      ...rawColumnDefinitions,
      {
        id: "teamName",
        header: "Team",
        cell: (item) => TEAM_NAME_MAP[item.teamName],
        ariaLabel: createLabelFunction("Team"),
        sortingField: "teamName",
        width: 120,
      },
    ];
    const guestBasicColumns = rawColumnDefinitions.filter((col) =>
      GUEST_AUDIT_FIELDS.includes(col.id as any)
    );
    return guestBasicColumns;
  }

  if (auditCustomAttributes && Array.isArray(auditCustomAttributes)) {
    const newColumns = auditCustomAttributes.map(attributeSchemaToColDef);
    const columnDefinitions = [...rawColumnDefinitions, ...newColumns];
    // Use the new columnDefinitions array
    return columnDefinitions;
  }

  return rawColumnDefinitions;
};

function attributeSchemaToColPrefOption(schema: any) {
  return {
    id: schema.attributeName,
    label: schema.fieldName,
  };
}

function attributeSchemaToColPrefOptionDefault(schema: any) {
  return {
    id: schema.attributeName,
    label: schema.fieldName,
    default: false,
  };
}

export const useContentDisplayPreferenceOptions = () => {
  const teamName = useAppSelector((state) => state.audits.headerSelectedTeam);
  const auditCustomAttributes = useAppSelector(
    (state) =>
      state.teamSettings.selectedTeamMainMetaData.teamCustomAttributesSchema
        ?.audit
  );

  let basicOptions: CollectionPreferencesProps.ContentDisplayOption[] = [
    { id: "shortId", label: "ID" },
    { id: "name", label: "Name", alwaysVisible: true },
    { id: "status", label: "Status" },
    { id: "owner", label: "Owner" },
    { id: "actionOwner", label: "Action pending" },
    { id: "progressUpdated", label: "Progress updated" },
    { id: "statusDetail", label: "Progress detail" },
    { id: "entity", label: "Entity" },
    { id: "entityCode", label: "Entity code" },
    { id: "authority", label: "Tax authority" },
    { id: "localJurisdiction", label: "Local jurisdiction" },
    { id: "stateProvince", label: "State/Province" },
    { id: "taxType", label: "Tax type" },
    { id: "auditPeriod", label: "Audit period" },
    { id: "auditInitiationDate", label: "Audit initiation date" },
    { id: "jurisdictionLevel", label: "Jurisdiction level" },
    { id: "auditNumber", label: "Audit number" },
    { id: "taxNumber", label: "Tax number" },
    { id: "auditCloseDate", label: "Audit close date" },
    { id: "statuteOfLimitations", label: "Statute of limitations" },
    { id: "dateCreated", label: "Date created" },
    { id: "lastModified", label: "Date modified" },
    { id: "description", label: "Description" },
    { id: "assessmentTotal", label: "Assessment Total" },
    { id: "paymentTotal", label: "Payment Total" },
    { id: "auditPeriodStart", label: "Audit period start" },
    { id: "auditPeriodEnd", label: "Audit period end" },
    { id: "progressModifiedBy", label: "Progress update by" },
    { id: "progressModifiedAt", label: "Progress update at" },
    { id: "effort", label: "Effort" },
  ];

  // For guest users, only return basic common options without custom attributes
  if (teamName === "guest") {
    basicOptions = [...basicOptions, { id: "teamName", label: "Team" }];
    const guestBasicOptions = basicOptions.filter((option) =>
      GUEST_AUDIT_FIELDS.includes(option.id as any)
    );
    return guestBasicOptions;
  }

  if (auditCustomAttributes) {
    const newOptions = auditCustomAttributes.map(
      attributeSchemaToColPrefOption
    );
    const options = [...basicOptions, ...newOptions];
    // Use the new columnDefinitions array
    return options;
  }
  return basicOptions;
};

export const useContentDisplayPreferenceOptionsDefault = () => {
  const teamName = useAppSelector((state) => state.audits.headerSelectedTeam);
  let basicOptionsDefault: { id: any; label: any; visible: boolean }[] = [
    { id: "shortId", label: "ID", visible: true },
    { id: "name", label: "Name", visible: true },
    { id: "status", label: "Status", visible: true },
    { id: "statusDetail", label: "Progress detail", visible: true },
    { id: "progressUpdated", label: "Progress updated", visible: true },
    { id: "actionOwner", label: "Action pending", visible: true },
    { id: "owner", label: "Owner", visible: true },
    {
      id: "auditInitiationDate",
      label: "Audit initiation date",
      visible: true,
    },
    { id: "auditCloseDate", label: "Audit close date", visible: true },
    {
      id: "statuteOfLimitations",
      label: "Statute of limitations",
      visible: true,
    },
    { id: "dateCreated", label: "Date created", visible: true },
    { id: "lastModified", label: "Date modified", visible: true },
    { id: "entity", label: "Entity", visible: false },
    { id: "entityCode", label: "Entity code", visible: false },
    { id: "authority", label: "Tax authority", visible: false },
    { id: "localJurisdiction", label: "Local jurisdiction", visible: false },
    { id: "stateProvince", label: "State/Province", visible: false },
    { id: "taxType", label: "Tax type", visible: false },
    { id: "auditPeriod", label: "Audit period", visible: false },
    { id: "jurisdictionLevel", label: "Jurisdiction level", visible: false },
    { id: "auditNumber", label: "Audit number", visible: false },
    { id: "taxNumber", label: "Tax number", visible: false },
    { id: "description", label: "Description", visible: false },
    { id: "assessmentTotal", label: "Assessment Total", visible: false },
    { id: "paymentTotal", label: "Payment Total", visible: false },
    { id: "auditPeriodStart", label: "Audit period start", visible: false },
    { id: "auditPeriodEnd", label: "Audit period end", visible: false },
    { id: "progressModifiedBy", label: "Progress Update By", visible: false },
    { id: "progressModifiedAt", label: "Progress Update At", visible: false },
    { id: "effort", label: "Effort", visible: false },
  ];
  const auditCustomAttributes = useAppSelector(
    (state) =>
      state.teamSettings.selectedTeamMainMetaData.teamCustomAttributesSchema
        ?.audit
  );
  return useMemo(() => {
    // For guest users, only return guest attributes
    if (teamName === "guest") {
      basicOptionsDefault = [
        ...basicOptionsDefault,
        { id: "teamName", label: "Team", visible: true },
      ];
      const guestBasicOptionsDefault = basicOptionsDefault.filter((option) =>
        GUEST_AUDIT_FIELDS.includes(option.id as any)
      );
      return guestBasicOptionsDefault;
    }

    if (auditCustomAttributes) {
      const newOptionsDefault = auditCustomAttributes.map(
        attributeSchemaToColPrefOptionDefault
      );
      const optionsDefault = [...basicOptionsDefault, ...newOptionsDefault];
      return optionsDefault;
    }
    return basicOptionsDefault;
  }, [teamName]);
};

export const useAuditPropertyKeyForMismatchedId = () => {
  const auditPropertyKeyForMismatchedId = new Map([
    ["name", "auditName"],
    ["actionOwner", "statusActionOwner"],
    ["authority", "taxAuthority"],
    ["stateProvince", "governmentEntity"],
    ["dateCreated", "createdAt"],
    ["lastModified", "modifiedAt"],
    ["auditPeriodStart", "auditStartDate"],
    ["auditPeriodEnd", "auditEndDate"],
  ]);
  return auditPropertyKeyForMismatchedId;
};

export const useAuditPropertyDateNames = () => {
  const auditPropertyDateNames = new Set([
    "auditCloseDate",
    "auditEndDate",
    "auditInitiationDate",
    "auditStartDate",
    "createdAt",
    "modifiedAt",
    "statuteOfLimitations",
    "auditPeriodStart",
    "auditPeriodEnd",
    "progressModifiedAt",
  ]);
  const auditCustomAttributes = useAppSelector(
    (state) =>
      state.teamSettings.selectedTeamMainMetaData.teamCustomAttributesSchema
        ?.audit
  );
  if (auditCustomAttributes && Array.isArray(auditCustomAttributes)) {
    auditCustomAttributes.forEach((attribute) => {
      if (attribute.inputValueType === "date") {
        auditPropertyDateNames.add(attribute.attributeName);
      }
    });
  }
  return auditPropertyDateNames;
};

export const useTeamSpecificAuditTemplates = () => {
  const getTemplatesByTeamName = new Map<string, TemplateOption[]>([
    [
      "gaia_dev",
      [
        {
          value: "",
          label: "\u00A0",
        },
        {
          value: "template#us_salt_indirect#Sales_Tax",
          label: "Sales Tax",
        },
        {
          value: "template#us_salt_indirect#Sales/Use_Tax",
          label: "Sales/Use Tax",
        },
        {
          value: "template#us_salt_indirect#Use_Tax",
          label: "Use Tax",
        },
        {
          value: "template#us_salt_indirect#Excise_Tax",
          label: "Excise Tax",
        },
        {
          value: "template#us_salt_indirect#Regulatory_Fee",
          label: "Regulatory Fee",
        },
        {
          value: "template#us_salt_indirect#Marketplace_Facilitator",
          label: "MPF",
        },
        {
          value: "template#us_salt_indirect#Telecom",
          label: "Telecom",
        },
        {
          value: "template#us_salt_direct#Income_Tax",
          label: "Income Tax",
        },
        {
          value: "template#us_salt_direct#Franchise_Tax",
          label: "Franchise Tax",
        },
        {
          value: "template#us_salt_direct#Gross_Receipts_Tax",
          label: "Gross Receipts Tax",
        },
        {
          value: "template#us_salt_direct#Nexus",
          label: "Nexus",
        },
        {
          value: "template#us_salt_direct#Other",
          label: "Other",
        },
        {
          value: "template#us_property_tax#Property_Tax_Standard_Audit",
          label: "Standard Audit",
        },
      ],
    ],
    [
      "us_salt_indirect",
      [
        {
          value: "",
          label: "\u00A0",
        },
        {
          value: "template#us_salt_indirect#Sales_Tax",
          label: "Sales Tax",
        },
        {
          value: "template#us_salt_indirect#Sales/Use_Tax",
          label: "Sales/Use Tax",
        },
        {
          value: "template#us_salt_indirect#Use_Tax",
          label: "Use Tax",
        },
        {
          value: "template#us_salt_indirect#Excise_Tax",
          label: "Excise Tax",
        },
        {
          value: "template#us_salt_indirect#Regulatory_Fee",
          label: "Regulatory Fee",
        },
        {
          value: "template#us_salt_indirect#Marketplace_Facilitator",
          label: "MPF",
        },
        {
          value: "template#us_salt_indirect#Telecom",
          label: "Telecom",
        },
      ],
    ],
    [
      "us_salt_direct",
      [
        {
          value: "",
          label: "\u00A0",
        },
        {
          value: "template#us_salt_direct#Income_Tax",
          label: "Income Tax",
        },
        {
          value: "template#us_salt_direct#Franchise_Tax",
          label: "Franchise Tax",
        },
        {
          value: "template#us_salt_direct#Gross_Receipts_Tax",
          label: "Gross Receipts Tax",
        },
        {
          value: "template#us_salt_direct#Nexus",
          label: "Nexus",
        },
        {
          value: "template#us_salt_direct#Other",
          label: "Other",
        },
      ],
    ],
    [
      "us_property_tax",
      [
        {
          value: "template#us_property_tax#Property_Tax_Standard_Audit",
          label: "Standard Audit",
        },
      ],
    ],
  ]);
  return getTemplatesByTeamName;
};
